import { ShoppingBag } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Loading Skeleton
const OrderSkeleton = () => (
  <div className="h-32 skeleton-pulse rounded-lg"></div>
);

// Empty State
const EmptyState = () => (
  <Card>
    <CardContent className="py-12 text-center">
      <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
      <h3 className="font-semibold mb-2">No orders yet</h3>
      <p className="text-muted-foreground">Orders will appear here when customers make purchases</p>
    </CardContent>
  </Card>
);

// Payment Status Badge
const PaymentStatusBadge = ({ status }) => {
  const getStatusClass = () => {
    switch (status) {
      case 'paid':
        return 'bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/20';
      case 'pending':
        return 'bg-[#eab308]/10 text-[#eab308] border-[#eab308]/20';
      default:
        return 'bg-destructive/10 text-destructive border-destructive/20';
    }
  };

  return <Badge className={getStatusClass()}>{status}</Badge>;
};

// Order Item
const OrderItem = ({ order, onUpdateStatus }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm text-muted-foreground">
              #{order.id.slice(0, 8)}
            </span>
            <PaymentStatusBadge status={order.payment_status} />
            <Badge variant="outline">{order.status}</Badge>
          </div>
          <p className="font-semibold">{order.customer_name}</p>
          <p className="text-sm text-muted-foreground">{order.customer_email}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {order.items?.length || 0} items • ${order.total?.toFixed(2)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={order.status}
            onValueChange={(value) => onUpdateStatus(order.id, value)}
          >
            <SelectTrigger className="w-32" data-testid={`order-status-${order.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </CardContent>
  </Card>
);

const OrderList = ({ orders, loading, onUpdateStatus }) => {
  if (loading) {
    return (
      <div className="grid gap-4">
        {[1, 2, 3].map((i) => <OrderSkeleton key={`skeleton-${i}`} />)}
      </div>
    );
  }

  if (orders.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((order) => (
        <OrderItem 
          key={order.id} 
          order={order} 
          onUpdateStatus={onUpdateStatus} 
        />
      ))}
    </div>
  );
};

export default OrderList;
